/**
 * StockHomologationDetailsDialog - Detalhes de uma homologação (somente leitura).
 *
 * Homologações são imutáveis (BR-EST-041): este modal apenas exibe o registro,
 * sem qualquer ação de edição ou exclusão.
 *
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial: abre ao clicar na linha da listagem.
 */
import { ArrowRight, BadgeCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { HomologationRow } from '@/hooks/useStockHomologations';
import { homologationResultLabel, homologationTypeLabel } from '@/lib/stock-homologation';
import { formatStockDateTime, stockStatusLabel } from '@/lib/stock-presentation';
import { cn } from '@/lib/utils';

interface Props {
  item: HomologationRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Linha de detalhe: rótulo + valor */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm text-right">{children}</span>
    </div>
  );
}

export function StockHomologationDetailsDialog({ item, open, onOpenChange }: Props) {
  const isApproved = item?.result === 'approved';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-primary" />
            Homologação {item?.number}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-1">
            <DetailRow label="Rastreador">
              <span className="font-mono">{item.imei}</span>
              {item.model_name && <span className="block text-xs text-muted-foreground">{item.model_name}</span>}
            </DetailRow>
            <DetailRow label="Tipo">{homologationTypeLabel(item.homologation_type)}</DetailRow>
            <DetailRow label="Resultado">
              <Badge
                variant="outline"
                className={cn(
                  'font-normal',
                  isApproved
                    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                    : 'bg-destructive/10 text-destructive border-destructive/20',
                )}
              >
                {homologationResultLabel(item.result)}
              </Badge>
            </DetailRow>
            <DetailRow label="Situação">
              {item.status_before === item.status_after ? (
                <span className="text-muted-foreground">{stockStatusLabel('tracker', item.status_before)} (sem alteração)</span>
              ) : (
                <span className="inline-flex items-center gap-1">
                  {stockStatusLabel('tracker', item.status_before)}
                  <ArrowRight className="h-3 w-3" />
                  <b>{stockStatusLabel('tracker', item.status_after)}</b>
                </span>
              )}
            </DetailRow>
            <DetailRow label="Plataforma">{item.platform_name ?? '—'}</DetailRow>
            <DetailRow label="Responsável">{item.created_by_name ?? '—'}</DetailRow>
            <DetailRow label="Data">{formatStockDateTime(item.created_at)}</DetailRow>
            {item.notes && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-1">Observação</p>
                <p className="text-sm whitespace-pre-wrap rounded-lg bg-muted/40 p-3">{item.notes}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
